import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { 
  Star,
  User,
  Clock,
  Search,
  Filter,
  Download,
  MoreHorizontal,
  Edit,
  Trash2,
  Eye,
  Mail,
  Phone,
  MapPin,
  Calendar,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  UserX,
  Award,
  TrendingUp,
  Activity,
  Users,
  CheckCircle,
  XCircle,
  AlertCircle,
  Crown,
  Shield
} from 'lucide-react'
import { format } from 'date-fns'
import ApiService from '../services/api'
import toast from 'react-hot-toast'

function Yajman() {
  const navigate = useNavigate()
  const [yajmans, setYajmans] = useState([])
  const [stats, setStats] = useState({
    totalYajmans: 0,
    activeYajmans: 0,
    inactiveYajmans: 0,
    premiumYajmans: 0,
    newYajmansThisMonth: 0,
    yajmansByCity: {},
    yajmansByJoinDate: {}
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [filterType, setFilterType] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(10)
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNext: false,
    hasPrev: false
  })

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Reset to first page when search term changes
  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearchTerm, filterStatus, filterType])

  // Load yajman stats only once on component mount
  useEffect(() => {
    fetchYajmanStats()
  }, [])

  // Function to refresh data
  const fetchYajmansWithAbort = async () => {
    try {
      setLoading(true)
      setError(null)
      const params = new URLSearchParams({
        page: currentPage.toString(),
        limit: itemsPerPage.toString(),
        ...(debouncedSearchTerm && { search: debouncedSearchTerm }),
        ...(filterStatus !== 'all' && { status: filterStatus }),
        ...(filterType !== 'all' && { type: filterType }),
        sortBy: 'createdAt',
        sortOrder: 'desc'
      })

      const response = await ApiService.getAllYajmans(params)
      setYajmans(response.data || [])
      setPagination(response.pagination || {})
    } catch (error) {
      console.error('Error fetching yajmans:', error)
      setError(error.message || 'Failed to load yajmans')
      toast.error('Failed to load yajmans')
      setYajmans([])
      setPagination({
        page: 1,
        limit: 10,
        total: 0,
        totalPages: 0,
        hasNext: false,
        hasPrev: false
      })
    } finally {
      setLoading(false)
    }
  }

  // Load yajmans data when filters change
  useEffect(() => {
    fetchYajmansWithAbort()
  }, [currentPage, debouncedSearchTerm, filterStatus, filterType, itemsPerPage])

  const fetchYajmanStats = async () => {
    try {
      const response = await ApiService.getYajmanStats()
      setStats(response.data || {})
    } catch (error) {
      console.error('Error fetching yajman stats:', error)
      toast.error('Failed to load yajman statistics')
      // Keep default empty stats with mock data
      setStats({
        totalYajmans: 0,
        activeYajmans: 0,
        inactiveYajmans: 0,
        premiumYajmans: 0,
        newYajmansThisMonth: 0,
        yajmansByCity: {},
        yajmansByJoinDate: {}
      })
    }
  }

  const handleUpdateYajmanStatus = async (yajmanId, status) => {
    try {
      await ApiService.updateYajmanStatus(yajmanId, status)
      toast.success(`Yajman ${status.toLowerCase()} successfully`)
      // Refresh the data instead of full page reload
      fetchYajmansWithAbort()
    } catch (error) {
      console.error('Error updating yajman status:', error)
      toast.error('Failed to update yajman status')
    }
  }

  const handleDeleteYajman = async (yajmanId) => {
    if (window.confirm('Are you sure you want to delete this yajman?')) {
      try {
        await ApiService.deleteYajman(yajmanId)
        toast.success('Yajman deleted successfully')
        // Refresh the data instead of full page reload
        fetchYajmansWithAbort()
      } catch (error) {
        console.error('Error deleting yajman:', error)
        toast.error('Failed to delete yajman')
      }
    }
  }


  const exportYajmans = () => {
    toast.success('Export functionality coming soon!')
  }

  const filteredYajmans = yajmans

  const statCards = [
    {
      title: 'Total Yajmans',
      value: stats.totalYajmans ,
      icon: Users,
      color: 'bg-blue-500',
      change: '+8%',
      changeType: 'positive',
      description: 'Registered customers'
    },
    {
      title: 'Active Users',
      value: stats.activeYajmans,
      icon: UserCheck,
      color: 'bg-green-500',
      change: '+12%',
      changeType: 'positive',
      description: 'Currently active'
    },
    {
      title: 'Premium Members',
      value: stats.premiumYajmans ,
      icon: Crown,
      color: 'bg-yellow-500',
      change: '+15%',
      changeType: 'positive',
      description: 'Premium subscriptions'
    },
    {
      title: 'New This Month',
      value: stats.newYajmansThisMonth,
      icon: TrendingUp,
      color: 'bg-purple-500',
      change: '+22%',
      changeType: 'positive',
      description: 'Recent registrations'
    }
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center">
            Yajman Management
          </h1>
          <p className="mt-2 text-gray-600">
            Manage all yajman (customer) accounts, subscriptions, and activities
          </p>
        </div>
        <div className="mt-4 sm:mt-0 flex space-x-3">
          <button
            onClick={exportYajmans}
            className="inline-flex items-center px-4 py-2 bg-white border border-gray-300 text-gray-700 font-medium rounded-lg hover:bg-gray-50 transition-colors duration-200"
          >
            <Download className="w-4 h-4 mr-2" />
            Export
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat, index) => {
          const Icon = stat.icon
          return (
            <div key={index} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-16 h-16 opacity-5">
                <div className="w-full h-full bg-gradient-to-br from-blue-400 to-purple-500 rounded-full transform translate-x-8 -translate-y-8"></div>
              </div>
              
              <div className="flex items-center justify-between relative z-10">
                <div>
                  <p className="text-sm font-medium text-gray-600">{stat.title}</p>
                  <p className="text-3xl font-bold text-gray-900 mt-2">{stat.value}</p>
                  <p className="text-xs text-gray-500 mt-1">{stat.description}</p>
                </div>
                <div className={`${stat.color} p-3 rounded-lg shadow-sm`}>
                  <Icon className="w-6 h-6 text-white" />
                </div>
              </div>
              <div className="mt-4 flex items-center">
                <span className={`text-sm font-medium ${
                  stat.changeType === 'positive' ? 'text-green-600' : 
                  stat.changeType === 'attention' ? 'text-orange-600' : 'text-gray-600'
                }`}>
                  {stat.change}
                </span>
                <span className="text-sm text-gray-500 ml-2">vs last month</span>
              </div>
            </div>
          )
        })}
      </div>

      {/* Filters and Search */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search yajmans..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
              <option value="SUSPENDED">Suspended</option>
            </select>
            
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="premium">Premium</option>
              <option value="regular">Regular</option>
            </select>
          </div>
          
          <div className="text-sm text-gray-600">
            Showing {pagination.total ? `${(pagination.page - 1) * pagination.limit + 1} - ${Math.min(pagination.page * pagination.limit, pagination.total)} of ${pagination.total}` : '0'} yajmans
          </div>
        </div>
      </div>

      {/* Yajmans Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">All Yajmans</h3>
        </div>
        
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-2 text-gray-500">Loading yajmans...</p>
          </div>
        ) : error ? (
          <div className="p-8 text-center">
            <div className="text-red-500 mb-4">
              <svg className="w-12 h-12 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Yajmans</h3>
            <p className="text-gray-500 mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
            >
              Try Again
            </button>
          </div>
        ) : filteredYajmans.length === 0 ? (
          <div className="p-8 text-center">
            <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No yajmans found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact & Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Account Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Activity
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Joined
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredYajmans.map((yajman) => (
                  <tr 
                    key={yajman.id} 
                    className="hover:bg-gray-50 transition-colors duration-150 cursor-pointer"
                    onClick={() => navigate(`/yajman/${yajman.id}`)}
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-400 to-purple-500 flex items-center justify-center relative">
                            <span className="text-white font-semibold text-sm">
                              {yajman.name?.charAt(0)?.toUpperCase() || yajman.email?.charAt(0)?.toUpperCase()}
                            </span>
                            {yajman.isPremium && (
                              <div className="absolute -top-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full flex items-center justify-center">
                                <Crown className="w-2 h-2 text-white" />
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 flex items-center">
                            {yajman.name || 'No name provided'}
                            {yajman.isPremium && (
                              <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                                Premium
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500">
                            {yajman.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        {yajman.phone && (
                          <div className="flex items-center text-sm text-gray-600">
                            <Phone className="w-3 h-3 mr-1" />
                            {yajman.phone}
                          </div>
                        )}
                        <div className="flex items-center">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            yajman.status === 'ACTIVE' 
                              ? 'bg-green-100 text-green-800'
                              : yajman.status === 'INACTIVE'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {yajman.status || 'UNKNOWN'}
                          </span>
                        </div>
                        {yajman.city && (
                          <div className="text-xs text-gray-500 flex items-center">
                            <MapPin className="w-3 h-3 mr-1" />
                            {yajman.city}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {yajman.isPremium ? (
                          <div className="flex items-center text-yellow-600">
                            <Crown className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">Premium</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-gray-600">
                            <User className="w-4 h-4 mr-1" />
                            <span className="text-sm font-medium">Regular</span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="space-y-1">
                        <div className="text-sm text-gray-900">
                          {yajman.totalBookings || 0} bookings
                        </div>
                        <div className="text-xs text-gray-500">
                          Last active: {yajman.lastLoginAt ? format(new Date(yajman.lastLoginAt), 'MMM dd') : 'Never'}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 flex items-center">
                        <Calendar className="w-3 h-3 mr-1 text-gray-400" />
                        {format(new Date(yajman.createdAt), 'MMM dd, yyyy')}
                      </div>
                      <div className="text-xs text-gray-500">
                        {format(new Date(yajman.createdAt), 'HH:mm')}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/yajman/${yajman.id}`)
                          }}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors duration-150"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        {/* <button
                          onClick={(e) => e.stopPropagation()}
                          className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50 transition-colors duration-150"
                          title="Edit User"
                        >
                          <Edit className="w-4 h-4" />
                        </button> */}
                        <div className="relative group">
                          <button 
                            onClick={(e) => e.stopPropagation()}
                            className="text-gray-400 hover:text-gray-600 p-1 rounded-full hover:bg-gray-50 transition-colors duration-150"
                          >
                            <MoreHorizontal className="w-4 h-4" />
                          </button>
                          <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg z-10 border border-gray-200 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200">
                            <div className="py-1">
                              {yajman.status === 'ACTIVE' ? (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleUpdateYajmanStatus(yajman.id, 'INACTIVE')
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-orange-700 hover:bg-orange-50"
                                >
                                  Deactivate User
                                </button>
                              ) : (
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation()
                                    handleUpdateYajmanStatus(yajman.id, 'ACTIVE')
                                  }}
                                  className="block w-full text-left px-4 py-2 text-sm text-green-700 hover:bg-green-50"
                                >
                                  Activate User
                                </button>
                              )}
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  navigate(`/yajman/${yajman.id}`)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-blue-700 hover:bg-blue-50"
                              >
                                View Profile
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteYajman(yajman.id)
                                }}
                                className="block w-full text-left px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                              >
                                Delete User
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {filteredYajmans.length > 0 && pagination.totalPages > 1 && (
          <div className="bg-white px-6 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={!pagination.hasPrev}
                  className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setCurrentPage(prev => prev + 1)}
                  disabled={!pagination.hasNext}
                  className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing{' '}
                    <span className="font-medium">
                      {(pagination.page - 1) * pagination.limit + 1}
                    </span>{' '}
                    to{' '}
                    <span className="font-medium">
                      {Math.min(pagination.page * pagination.limit, pagination.total)}
                    </span>{' '}
                    of{' '}
                    <span className="font-medium">{pagination.total}</span>{' '}
                    results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                      disabled={!pagination.hasPrev}
                      className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </button>
                    
                    {Array.from({ length: pagination.totalPages }, (_, i) => i + 1)
                      .filter(page => page === 1 || page === pagination.totalPages || Math.abs(page - currentPage) <= 1)
                      .map((page, index, array) => (
                        <React.Fragment key={page}>
                          {index > 0 && array[index - 1] !== page - 1 && (
                            <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                              ...
                            </span>
                          )}
                          <button
                            onClick={() => setCurrentPage(page)}
                            className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${
                              currentPage === page
                                ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                                : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'
                            }`}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      ))}
                    
                    <button
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      disabled={!pagination.hasNext}
                      className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronRight className="h-5 w-5" />
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default Yajman
